import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getFetchPlaylistLink,
  getPlaylist,
} from 'app/store/Playlists/selectors';
import axios from 'axios';
import {
  GetPlaylistResponse,
  PlaylistImage,
  PlayPlaylistContext,
} from 'types/playlists.types';
import {
  getAudioPlayer,
  getIsPlayerLoading,
  getPlaylistLoadingState,
} from 'app/store/AudioPlayer/selectors';
import { addAlertAction } from 'app/store/Alerts/slice';
import { transformToAudioPlayerAudio } from 'app/components/AudioPlayer/utils';
import {
  setPlaylistLoadingStateAction,
  setQueueIndexAction,
} from 'app/store/AudioPlayer/slice';
import { playlistPlayLocation } from 'app/store/Mixpanel/slice';
import { useAudioPlayer } from './useAudioPlayer';

export const usePlayerPlaylist = () => {
  const dispatch = useAppDispatch();
  const playlistState = useAppSelector(getPlaylist);
  const fetchPlaylistLink = useAppSelector(getFetchPlaylistLink);
  const { isPlaying, isOpen } = useAppSelector(getAudioPlayer);
  const isPlayerLoading = useAppSelector(getIsPlayerLoading);
  const { loadingPlaylist, loadingPlaylistGuid } =
    useAppSelector(getPlaylistLoadingState) || {};
  const { loadAudio, playPauseAudio } = useAudioPlayer();

  const loadPlaylist = async (playlistGuid: string, playIndex?: number) => {
    if (!fetchPlaylistLink) {
      return;
    }
    let playlist = playlistState;

    if (playlist?.guid !== playlistGuid) {
      try {
        const { data } = await axios.get<GetPlaylistResponse>(
          fetchPlaylistLink.href.replace('{playlistGuid}', playlistGuid),
          {
            withCredentials: true,
          }
        );
        playlist = data.response.playlist.item;
      } catch (e) {
        dispatch(
          addAlertAction({
            message: 'Failed to load Playlist. Please try again',
          })
        );
        throw e;
      }
    }

    await loadAudio({
      ...transformToAudioPlayerAudio(playlist),
      playIndex,
    });
  };

  const handler = async ({
    playlistGuid,
    playIndex,
    playedLocation,
    images,
  }: {
    playlistGuid: string;
    playIndex?: number;
    playedLocation: PlayPlaylistContext;
    images?: PlaylistImage[];
  }) => {
    if (loadingPlaylistGuid === playlistGuid && playIndex !== undefined) {
      dispatch(setQueueIndexAction(playIndex));
      return;
    }

    if (loadingPlaylistGuid === playlistGuid && isOpen) {
      playPauseAudio();
      return;
    }

    try {
      dispatch(
        setPlaylistLoadingStateAction({
          loadingPlaylist: true,
          loadingPlaylistGuid: playlistGuid,
          images,
        })
      );
      await loadPlaylist(playlistGuid, playIndex);
      dispatch(
        playlistPlayLocation({ playlistGuid, location: playedLocation })
      );
    } catch (e) {
      dispatch(
        setPlaylistLoadingStateAction({
          loadingPlaylistGuid: '',
        })
      );
    } finally {
      dispatch(
        setPlaylistLoadingStateAction({
          loadingPlaylist: false,
          images,
        })
      );
    }
  };

  return {
    loadPlaylist: handler,
    isPlayerLoading: isPlayerLoading || loadingPlaylist,
    loadingPlaylistGuid:
      isOpen || isPlayerLoading || loadingPlaylist ? loadingPlaylistGuid : '',
    isPlaying,
  };
};
